import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Eye, Edit, Trash2, Upload, Mail, CheckSquare, Filter, X, Calendar, Clock, Send, Check, MailCheck, Truck, Download, PackageCheck } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  DC_WARDS,
  VIRGINIA_CITIES,
  VIRGINIA_COUNTIES,
  MARYLAND_CITIES,
  MARYLAND_COUNTIES,
} from '@/utils/locationData';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

export default function FamilyList() {
  const [families, setFamilies] = useState([]);
  const [filteredFamilies, setFilteredFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [selectedFamilies, setSelectedFamilies] = useState([]);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [sendingMigration, setSendingMigration] = useState(false);
  const [permissions, setPermissions] = useState(null);
  
  // Location filters
  const [showFilters, setShowFilters] = useState(false);
  const [locationTypeFilter, setLocationTypeFilter] = useState('');
  const [wardFilter, setWardFilter] = useState('');
  const [virginiaCityFilter, setVirginiaCityFilter] = useState('');
  const [virginiaCountyFilter, setVirginiaCountyFilter] = useState('');
  const [marylandCityFilter, setMarylandCityFilter] = useState('');
  const [marylandCountyFilter, setMarylandCountyFilter] = useState('');
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('');
  
  // Delivery schedule management
  const [deliverySchedules, setDeliverySchedules] = useState({});
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [newDeliveryDate, setNewDeliveryDate] = useState('');
  const [newDeliveryTime, setNewDeliveryTime] = useState('');
  const [resendConfirmation, setResendConfirmation] = useState(true);
  const [updatingDelivery, setUpdatingDelivery] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  
  // Manual schedule delivery
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [familyToSchedule, setFamilyToSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduling, setScheduling] = useState(false);
  
  // Mark Delivered
  const [showDeliveredModal, setShowDeliveredModal] = useState(false);
  const [familyToMarkDelivered, setFamilyToMarkDelivered] = useState(null);
  const [deliveredDate, setDeliveredDate] = useState('');
  const [markingDelivered, setMarkingDelivered] = useState(false);
  const [resendingWelcome, setResendingWelcome] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);

  const timeSlots = [
    "9:00 AM - 11:00 AM",
    "11:00 AM - 1:00 PM",
    "1:00 PM - 3:00 PM",
    "3:00 PM - 5:00 PM"
  ];

  const handleExportCSV = async () => {
    setExportingData(true);
    try {
      // Build query params based on current filters
      const params = new URLSearchParams();
      if (locationTypeFilter) params.append('location_type', locationTypeFilter);
      if (deliveryStatusFilter) params.append('delivery_status', deliveryStatusFilter);
      
      const response = await api.get(`/families/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `families_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Family data exported successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export data');
    } finally {
      setExportingData(false);
    }
  };

  useEffect(() => {
    fetchFamilies();
    fetchPermissions();
    fetchDeliverySchedules();
    
    // Auto-refresh when user returns to this page/tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchFamilies();
        fetchDeliverySchedules();
      }
    };
    
    const handleFocus = () => {
      fetchFamilies();
      fetchDeliverySchedules();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await api.get('/auth/permissions');
      setPermissions(response.data.permissions);
    } catch (error) {
      console.error('Failed to fetch permissions');
    }
  };

  const fetchDeliverySchedules = async () => {
    try {
      // Add timestamp to prevent caching
      const response = await api.get(`/delivery-schedules?_t=${Date.now()}`);
      // Create a map of family_id -> schedule for quick lookup
      const scheduleMap = {};
      response.data.forEach(schedule => {
        if (schedule.status === 'confirmed' || schedule.status === 'pending') {
          scheduleMap[schedule.family_id] = schedule;
        }
      });
      setDeliverySchedules(scheduleMap);
    } catch (error) {
      console.error('Failed to fetch delivery schedules');
    }
  };

  useEffect(() => {
    let filtered = [...families];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((family) =>
        family.parent_first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        family.parent_last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        family.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (family.location_detail || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply location type filter
    if (locationTypeFilter) {
      filtered = filtered.filter((family) => family.location_type === locationTypeFilter);
    }
    
    // Apply DC Ward filter
    if (wardFilter) {
      filtered = filtered.filter((family) => {
        const familyWard = family.ward_number || (family.location_detail || '').replace('Ward ', '');
        return familyWard === wardFilter;
      });
    }
    
    // Apply Virginia city filter
    if (virginiaCityFilter) {
      filtered = filtered.filter((family) => {
        const city = family.virginia_city || family.location_detail || '';
        return city === virginiaCityFilter || (virginiaCityFilter === 'Other' && family.virginia_city_other);
      });
    }
    
    // Apply Virginia county filter
    if (virginiaCountyFilter) {
      filtered = filtered.filter((family) => {
        const county = family.virginia_county || family.city_county || '';
        return county === virginiaCountyFilter || (virginiaCountyFilter === 'Other' && family.virginia_county_other);
      });
    }
    
    // Apply Maryland city filter
    if (marylandCityFilter) {
      filtered = filtered.filter((family) => {
        const city = family.maryland_city || family.location_detail || '';
        return city === marylandCityFilter || (marylandCityFilter === 'Other' && family.maryland_city_other);
      });
    }
    
    // Apply Maryland county filter
    if (marylandCountyFilter) {
      filtered = filtered.filter((family) => {
        const county = family.maryland_county || family.city_county || '';
        return county === marylandCountyFilter || (marylandCountyFilter === 'Other' && family.maryland_county_other);
      });
    }
    
    // Apply delivery status filter
    if (deliveryStatusFilter) {
      filtered = filtered.filter((family) => family.delivery_status === deliveryStatusFilter);
    }
    
    setFilteredFamilies(filtered);
  }, [searchTerm, families, locationTypeFilter, wardFilter, virginiaCityFilter, virginiaCountyFilter, marylandCityFilter, marylandCountyFilter, deliveryStatusFilter]);
  
  // Clear all filters
  const clearFilters = () => {
    setLocationTypeFilter('');
    setWardFilter('');
    setVirginiaCityFilter('');
    setVirginiaCountyFilter('');
    setMarylandCityFilter('');
    setMarylandCountyFilter('');
    setDeliveryStatusFilter('');
    setSearchTerm('');
  };
  
  // Check if any filter is active
  const hasActiveFilters = locationTypeFilter || wardFilter || virginiaCityFilter || virginiaCountyFilter || marylandCityFilter || marylandCountyFilter || deliveryStatusFilter;
  
  // Handle location type change - reset sub-filters
  const handleLocationTypeChange = (value) => {
    setLocationTypeFilter(value);
    setWardFilter('');
    setVirginiaCityFilter('');
    setVirginiaCountyFilter('');
    setMarylandCityFilter('');
    setMarylandCountyFilter('');
  };

  // Open delivery management modal
  const openDeliveryModal = (family) => {
    setSelectedFamily(family);
    const schedule = deliverySchedules[family.id];
    setSelectedSchedule(schedule || null);
    if (schedule?.confirmed_slot) {
      setNewDeliveryDate(schedule.confirmed_slot.date);
      setNewDeliveryTime(schedule.confirmed_slot.time_slot);
    } else {
      setNewDeliveryDate('');
      setNewDeliveryTime('');
    }
    setResendConfirmation(true);
    setShowDeliveryModal(true);
  };

  // Update delivery schedule
  const handleUpdateDelivery = async () => {
    if (!newDeliveryDate || !newDeliveryTime) {
      toast.error('Please select both date and time');
      return;
    }

    setUpdatingDelivery(true);
    try {
      const response = await api.put(`/delivery-schedules/${selectedSchedule.id}`, {
        date: newDeliveryDate,
        time_slot: newDeliveryTime,
        resend_confirmation: resendConfirmation
      });
      
      toast.success(resendConfirmation ? 'Delivery updated and confirmation sent!' : 'Delivery updated successfully!');
      setShowDeliveryModal(false);
      
      // Force refresh the delivery schedules to get the updated data
      await fetchDeliverySchedules();
      
      // Also refresh families in case the scheduled_delivery_date was updated
      await fetchFamilies();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update delivery');
    } finally {
      setUpdatingDelivery(false);
    }
  };

  // Resend confirmation email
  const handleResendConfirmation = async () => {
    if (!selectedSchedule) return;
    
    setUpdatingDelivery(true);
    try {
      await api.post(`/delivery-schedules/${selectedSchedule.id}/resend-confirmation`);
      toast.success('Confirmation email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send confirmation');
    } finally {
      setUpdatingDelivery(false);
    }
  };

  // Get status indicators for a family
  const getStatusIndicators = (family) => {
    const indicators = [];
    
    // Welcome email sent (new registration)
    if (family.welcome_email_sent) {
      indicators.push({ label: 'Welcome Email Sent', color: 'bg-blue-100 text-blue-800', icon: MailCheck });
    }
    
    // Migration welcome email sent (imported family)
    if (family.migration_welcome_sent) {
      indicators.push({ label: 'Migration Welcome Sent', color: 'bg-indigo-100 text-indigo-800', icon: MailCheck });
    }
    
    // Ready to schedule (family clicked the button)
    if (family.ready_to_schedule) {
      indicators.push({ label: 'Ready to Schedule', color: 'bg-teal-100 text-teal-800', icon: Check });
    }
    
    // Has scheduled delivery
    const schedule = deliverySchedules[family.id];
    if (schedule) {
      if (schedule.status === 'confirmed') {
        indicators.push({ 
          label: `Delivery: ${schedule.confirmed_slot?.date}`, 
          color: 'bg-purple-100 text-purple-800', 
          icon: Calendar 
        });
      } else if (schedule.status === 'pending') {
        indicators.push({ label: 'Awaiting Time Selection', color: 'bg-orange-100 text-orange-800', icon: Clock });
      }
    }
    
    // Delivered
    if (family.delivery_status === 'delivered') {
      indicators.push({ label: 'Bookshelf Delivered', color: 'bg-green-100 text-green-800', icon: Truck });
    }
    
    return indicators;
  };

  const fetchFamilies = async () => {
    try {
      const response = await api.get('/families');
      setFamilies(response.data);
      setFilteredFamilies(response.data);
    } catch (error) {
      toast.error('Failed to fetch families');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await api.delete(`/families/${deleteId}`);
      toast.success('Family deleted successfully');
      fetchFamilies();
    } catch (error) {
      toast.error('Failed to delete family');
    } finally {
      setDeleteId(null);
    }
  };

  const toggleSelectFamily = (familyId) => {
    setSelectedFamilies(prev => 
      prev.includes(familyId) 
        ? prev.filter(id => id !== familyId)
        : [...prev, familyId]
    );
  };

  const selectAllVisible = () => {
    // Select families that don't have user accounts yet (no user_id or no migration_welcome_sent)
    const eligibleFamilies = filteredFamilies.filter(f => !f.user_id && !f.migration_welcome_sent);
    setSelectedFamilies(eligibleFamilies.map(f => f.id));
  };

  const clearSelection = () => {
    setSelectedFamilies([]);
  };

  const handleSendMigrationEmails = async () => {
    if (selectedFamilies.length === 0) {
      toast.error('Please select at least one family');
      return;
    }

    setSendingMigration(true);
    try {
      const response = await api.post('/families/send-migration-welcome', selectedFamilies);
      toast.success(response.data.message);
      setShowMigrationDialog(false);
      setSelectedFamilies([]);
      fetchFamilies(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send migration emails');
    } finally {
      setSendingMigration(false);
    }
  };

  const openScheduleModal = (family) => {
    setFamilyToSchedule(family);
    setScheduleDate('');
    setScheduleTime('');
    setShowScheduleModal(true);
  };

  const handleManualSchedule = async () => {
    if (!familyToSchedule || !scheduleDate || !scheduleTime) {
      toast.error('Please select both a date and time slot');
      return;
    }
    setScheduling(true);
    try {
      const response = await api.post('/admin/manual-schedule-delivery', {
        family_id: familyToSchedule.id,
        delivery_date: scheduleDate,
        delivery_time: scheduleTime
      });
      toast.success(response.data.message);
      setShowScheduleModal(false);
      setFamilyToSchedule(null);
      fetchFamilies();
      fetchDeliverySchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule delivery');
    } finally {
      setScheduling(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const openDeliveredModal = (family) => {
    setFamilyToMarkDelivered(family);
    setDeliveredDate(new Date().toISOString().split('T')[0]); // Default to today
    setShowDeliveredModal(true);
  };

  const handleMarkDelivered = async () => {
    if (!familyToMarkDelivered || !deliveredDate) {
      toast.error('Please select a delivery date');
      return;
    }
    setMarkingDelivered(true);
    try {
      const response = await api.post('/admin/mark-delivered', {
        family_id: familyToMarkDelivered.id,
        delivery_date: deliveredDate
      });
      toast.success(response.data.message);
      setShowDeliveredModal(false);
      setFamilyToMarkDelivered(null);
      fetchFamilies();
      fetchDeliverySchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as delivered');
    } finally {
      setMarkingDelivered(false);
    }
  };

  const resendWelcomeEmail = async (family) => {
    setResendingWelcome(family.id);
    try {
      const response = await api.post(`/admin/resend-welcome-email/${family.id}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend welcome email');
    } finally {
      setResendingWelcome(null);
    }
  };

  const resendSchedulingInvitation = async (family) => {
    setResendingInvite(family.id);
    try {
      const response = await api.post(`/admin/resend-scheduling-invitation/${family.id}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend scheduling invitation');
    } finally {
      setResendingInvite(null);
    }
  };

  // Get count of families without user accounts
  const familiesWithoutAccounts = filteredFamilies.filter(f => !f.user_id && !f.migration_welcome_sent).length;

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="family-list-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Families</h1>
              <p className="text-stone-600">Manage family profiles and delivery status</p>
            </div>
            <div className="flex gap-2">
              {permissions?.can_import_families && (
                <Link to="/families/import">
                  <Button variant="outline" className="rounded-full" data-testid="import-families-button">
                    <Upload className="w-4 h-4 mr-2" />
                    Import Excel
                  </Button>
                </Link>
              )}
              {permissions?.can_send_announcements && familiesWithoutAccounts > 0 && (
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={() => setShowMigrationDialog(true)}
                  data-testid="send-migration-emails-button"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Welcome Emails ({familiesWithoutAccounts})
                </Button>
              )}
              {permissions?.can_view_reports && (
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={handleExportCSV}
                  disabled={exportingData}
                  data-testid="export-csv-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exportingData ? 'Exporting...' : 'Export CSV'}
                </Button>
              )}
              <Link to="/families/add">
                <Button className="rounded-full" data-testid="add-family-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Family
                </Button>
              </Link>
            </div>
          </div>

          <Card className="border-stone-200 shadow-card mb-6">
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Search and Filter Toggle */}
                <div className="flex gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400 w-5 h-5" />
                    <Input
                      data-testid="search-input"
                      type="text"
                      placeholder="Search by name, email, or location..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-11"
                    />
                  </div>
                  <Button
                    variant={showFilters ? "default" : "outline"}
                    onClick={() => setShowFilters(!showFilters)}
                    className="h-11"
                    data-testid="toggle-filters-button"
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                    {hasActiveFilters && (
                      <Badge className="ml-2 bg-white text-primary">Active</Badge>
                    )}
                  </Button>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      onClick={clearFilters}
                      className="h-11"
                      data-testid="clear-filters-button"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Clear
                    </Button>
                  )}
                </div>
                
                {/* Filter Dropdowns */}
                {showFilters && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-stone-50 rounded-lg" data-testid="filter-panel">
                    {/* Location Type */}
                    <div>
                      <label className="text-sm font-medium text-stone-600 mb-1 block">Location Type</label>
                      <select
                        data-testid="filter-location-type"
                        value={locationTypeFilter}
                        onChange={(e) => handleLocationTypeChange(e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-sm"
                      >
                        <option value="">All Locations</option>
                        <option value="DC Ward">DC Ward</option>
                        <option value="Virginia">Virginia</option>
                        <option value="Maryland">Maryland</option>
                      </select>
                    </div>
                    
                    {/* Delivery Status */}
                    <div>
                      <label className="text-sm font-medium text-stone-600 mb-1 block">Delivery Status</label>
                      <select
                        data-testid="filter-delivery-status"
                        value={deliveryStatusFilter}
                        onChange={(e) => setDeliveryStatusFilter(e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-sm"
                      >
                        <option value="">All Statuses</option>
                        <option value="pending">Awaiting Delivery</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </div>
                    
                    {/* DC Ward Filter */}
                    {locationTypeFilter === 'DC Ward' && (
                      <div>
                        <label className="text-sm font-medium text-stone-600 mb-1 block">DC Ward</label>
                        <select
                          data-testid="filter-ward"
                          value={wardFilter}
                          onChange={(e) => setWardFilter(e.target.value)}
                          className="w-full h-10 px-3 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-sm"
                        >
                          <option value="">All Wards</option>
                          {DC_WARDS.map(ward => (
                            <option key={ward.value} value={ward.value}>{ward.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Virginia Filters */}
                    {locationTypeFilter === 'Virginia' && (
                      <>
                        <div>
                          <label className="text-sm font-medium text-stone-600 mb-1 block">Virginia City</label>
                          <select
                            data-testid="filter-virginia-city"
                            value={virginiaCityFilter}
                            onChange={(e) => setVirginiaCityFilter(e.target.value)}
                            className="w-full h-10 px-3 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-sm"
                          >
                            <option value="">All Cities</option>
                            {VIRGINIA_CITIES.filter(c => c.value).map(city => (
                              <option key={city.value} value={city.value}>{city.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-stone-600 mb-1 block">Virginia County</label>
                          <select
                            data-testid="filter-virginia-county"
                            value={virginiaCountyFilter}
                            onChange={(e) => setVirginiaCountyFilter(e.target.value)}
                            className="w-full h-10 px-3 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-sm"
                          >
                            <option value="">All Counties</option>
                            {VIRGINIA_COUNTIES.filter(c => c.value).map(county => (
                              <option key={county.value} value={county.value}>{county.label}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                    
                    {/* Maryland Filters */}
                    {locationTypeFilter === 'Maryland' && (
                      <>
                        <div>
                          <label className="text-sm font-medium text-stone-600 mb-1 block">Maryland City</label>
                          <select
                            data-testid="filter-maryland-city"
                            value={marylandCityFilter}
                            onChange={(e) => setMarylandCityFilter(e.target.value)}
                            className="w-full h-10 px-3 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-sm"
                          >
                            <option value="">All Cities</option>
                            {MARYLAND_CITIES.filter(c => c.value).map(city => (
                              <option key={city.value} value={city.value}>{city.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-stone-600 mb-1 block">Maryland County</label>
                          <select
                            data-testid="filter-maryland-county"
                            value={marylandCountyFilter}
                            onChange={(e) => setMarylandCountyFilter(e.target.value)}
                            className="w-full h-10 px-3 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-sm"
                          >
                            <option value="">All Counties</option>
                            {MARYLAND_COUNTIES.filter(c => c.value).map(county => (
                              <option key={county.value} value={county.value}>{county.label}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                {/* Results Count */}
                <div className="flex items-center justify-between text-sm text-stone-500">
                  <span data-testid="results-count">
                    Showing {filteredFamilies.length} of {families.length} families
                  </span>
                  {hasActiveFilters && (
                    <span className="text-primary font-medium">Filters applied</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : filteredFamilies.length === 0 ? (
            <Card className="border-stone-200 shadow-card">
              <CardContent className="py-12 text-center">
                <p className="text-stone-600">No families found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="families-list">
              {filteredFamilies.map((family) => {
                const statusIndicators = getStatusIndicators(family);
                const hasDeliverySchedule = !!deliverySchedules[family.id];
                
                return (
                <Card key={family.id} className="border-stone-200 shadow-card hover:shadow-floating transition-all" data-testid={`family-card-${family.id}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-foreground">
                            {family.parent_first_name} {family.parent_last_name}
                          </h3>
                          <Badge
                            data-testid={`status-badge-${family.id}`}
                            className={
                              family.delivery_status === 'delivered' 
                                ? 'bg-green-100 text-green-800' 
                                : family.delivery_status === 'scheduled'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }
                          >
                            {family.delivery_status === 'pending' 
                              ? 'Awaiting Delivery' 
                              : family.delivery_status === 'delivered'
                              ? 'Delivered'
                              : family.delivery_status === 'scheduled'
                              ? 'Scheduled'
                              : family.delivery_status}
                          </Badge>
                          {!family.user_id && !family.migration_welcome_sent && (
                            <Badge variant="outline" className="bg-stone-100 text-stone-600 border-stone-300">
                              No Portal Account
                            </Badge>
                          )}
                          {family.is_historical && (
                            <Badge
                              variant="outline"
                              className="bg-amber-50 text-amber-800 border-amber-300"
                              data-testid={`historical-badge-${family.id}`}
                            >
                              Historical
                            </Badge>
                          )}
                        </div>
                        
                        {/* Status Indicators */}
                        {statusIndicators.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3" data-testid={`status-indicators-${family.id}`}>
                            {statusIndicators.map((indicator, idx) => (
                              <span
                                key={idx}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${indicator.color}`}
                              >
                                <indicator.icon className="w-3 h-3" />
                                {indicator.label}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <div className="text-sm text-stone-600 space-y-1">
                          <p>{family.email} • {family.phone}</p>
                          <p>{family.location_type}: {family.location_detail}</p>
                          <p>{family.children.length} child{family.children.length !== 1 ? 'ren' : ''}</p>
                          {family.created_at && (
                            <p className="text-stone-500">
                              Joined: {new Date(family.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <Link to={`/families/${family.id}`}>
                            <Button variant="outline" size="sm" data-testid={`view-button-${family.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link to={`/families/edit/${family.id}`}>
                            <Button variant="outline" size="sm" data-testid={`edit-button-${family.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteId(family.id)}
                            data-testid={`delete-button-${family.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {hasDeliverySchedule && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openDeliveryModal(family)}
                            className="w-full"
                            data-testid={`manage-delivery-${family.id}`}
                          >
                            <Calendar className="w-4 h-4 mr-2" />
                            Manage Delivery
                          </Button>
                        )}
                        {!hasDeliverySchedule && family.delivery_status !== 'delivered' && (
                          <Button
                            size="sm"
                            onClick={() => openScheduleModal(family)}
                            className="w-full rounded-full"
                            data-testid={`schedule-delivery-${family.id}`}
                          >
                            <Calendar className="w-4 h-4 mr-2" />
                            Schedule Delivery
                          </Button>
                        )}
                        {family.delivery_status !== 'delivered' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openDeliveredModal(family)}
                            className="w-full rounded-full text-green-700 border-green-200 hover:bg-green-50"
                            data-testid={`mark-delivered-${family.id}`}
                          >
                            <PackageCheck className="w-4 h-4 mr-2" />
                            Mark Delivered
                          </Button>
                        )}
                        {family.delivery_status === 'delivered' && (
                          <Badge className="bg-green-100 text-green-800 w-full justify-center py-1.5">
                            <PackageCheck className="w-3 h-3 mr-1" />
                            Delivered {family.delivered_at ? `on ${family.delivered_at}` : ''}
                          </Badge>
                        )}
                        {/* Resend Email Buttons */}
                        <div className="flex gap-2 w-full">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resendWelcomeEmail(family)}
                            disabled={resendingWelcome === family.id}
                            data-testid={`resend-welcome-${family.id}`}
                            className="flex-1 rounded-full text-xs h-8"
                          >
                            <Mail className="w-3 h-3 mr-1" />
                            {resendingWelcome === family.id ? '...' : 'Resend Welcome'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resendSchedulingInvitation(family)}
                            disabled={resendingInvite === family.id}
                            data-testid={`resend-invite-${family.id}`}
                            className="flex-1 rounded-full text-xs h-8"
                          >
                            <Send className="w-3 h-3 mr-1" />
                            {resendingInvite === family.id ? '...' : 'Resend Invite'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the family profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Migration Welcome Email Dialog */}
      <Dialog open={showMigrationDialog} onOpenChange={setShowMigrationDialog}>
        <DialogContent className="max-w-2xl" data-testid="migration-dialog">
          <DialogHeader>
            <DialogTitle>Send Migration Welcome Emails</DialogTitle>
            <DialogDescription>
              Send welcome emails with temporary login credentials to families who were imported but don't have user accounts yet.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-stone-600">
                {familiesWithoutAccounts} families need welcome emails
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAllVisible}>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Select All ({familiesWithoutAccounts})
                </Button>
                {selectedFamilies.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearSelection}>
                    Clear Selection
                  </Button>
                )}
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {filteredFamilies.filter(f => !f.user_id && !f.migration_welcome_sent).map(family => (
                <label
                  key={family.id}
                  className="flex items-center p-3 border-b last:border-b-0 hover:bg-stone-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedFamilies.includes(family.id)}
                    onChange={() => toggleSelectFamily(family.id)}
                    className="mr-3 h-4 w-4 rounded border-stone-300"
                  />
                  <div className="flex-1">
                    <p className="font-medium">{family.parent_first_name} {family.parent_last_name}</p>
                    <p className="text-sm text-stone-500">{family.email}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Ward {family.ward_number || family.location_detail}
                  </Badge>
                </label>
              ))}
            </div>
            
            {selectedFamilies.length > 0 && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>{selectedFamilies.length}</strong> families will receive welcome emails with:
                </p>
                <ul className="text-sm text-green-700 mt-2 ml-4 list-disc">
                  <li>A thank you message for registering</li>
                  <li>Their temporary username and password</li>
                  <li>Instructions to log in and set a new password</li>
                </ul>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMigrationDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendMigrationEmails}
              disabled={sendingMigration || selectedFamilies.length === 0}
              className="rounded-full"
            >
              {sendingMigration ? 'Sending...' : `Send Welcome Emails (${selectedFamilies.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery Management Modal */}
      <Dialog open={showDeliveryModal} onOpenChange={setShowDeliveryModal}>
        <DialogContent className="max-w-md" data-testid="delivery-modal">
          <DialogHeader>
            <DialogTitle>Manage Delivery Schedule</DialogTitle>
            <DialogDescription>
              {selectedFamily ? `${selectedFamily.parent_first_name} ${selectedFamily.parent_last_name}` : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {selectedSchedule ? (
              <>
                {/* Current Schedule Info */}
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-sm font-medium text-stone-600 mb-2">Current Schedule</p>
                  <div className="flex items-center gap-2 text-foreground">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span>{selectedSchedule.confirmed_slot?.date || 'Not set'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-foreground mt-1">
                    <Clock className="w-4 h-4 text-primary" />
                    <span>{selectedSchedule.confirmed_slot?.time_slot || 'Not set'}</span>
                  </div>
                </div>

                {/* Update Schedule */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-stone-600">Update Schedule</p>
                  
                  <div>
                    <Label htmlFor="delivery-date">New Delivery Date</Label>
                    <Input
                      id="delivery-date"
                      type="date"
                      value={newDeliveryDate}
                      onChange={(e) => setNewDeliveryDate(e.target.value)}
                      className="mt-1"
                      data-testid="delivery-date-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="delivery-time">New Time Slot</Label>
                    <select
                      id="delivery-time"
                      value={newDeliveryTime}
                      onChange={(e) => setNewDeliveryTime(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 mt-1"
                      data-testid="delivery-time-select"
                    >
                      <option value="">Select time slot</option>
                      {timeSlots.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="resend-confirmation"
                      checked={resendConfirmation}
                      onCheckedChange={setResendConfirmation}
                      data-testid="resend-confirmation-checkbox"
                    />
                    <Label htmlFor="resend-confirmation" className="text-sm font-normal cursor-pointer">
                      Send updated confirmation email to family
                    </Label>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-stone-500">
                No delivery schedule found for this family.
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {selectedSchedule && (
              <>
                <Button
                  variant="outline"
                  onClick={handleResendConfirmation}
                  disabled={updatingDelivery}
                  data-testid="resend-only-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Resend Current Confirmation
                </Button>
                <Button
                  onClick={handleUpdateDelivery}
                  disabled={updatingDelivery || !newDeliveryDate || !newDeliveryTime}
                  data-testid="update-delivery-button"
                >
                  {updatingDelivery ? 'Updating...' : 'Update Schedule'}
                </Button>
              </>
            )}
            {!selectedSchedule && (
              <Button variant="outline" onClick={() => setShowDeliveryModal(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Schedule Delivery Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="sm:max-w-md" data-testid="manual-schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Delivery</DialogTitle>
            <DialogDescription>
              Manually schedule a delivery for {familyToSchedule?.parent_first_name} {familyToSchedule?.parent_last_name}. 
              A confirmation email will be sent to the family.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {familyToSchedule && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-blue-900">{familyToSchedule.parent_first_name} {familyToSchedule.parent_last_name}</p>
                <p className="text-sm text-blue-700">{familyToSchedule.email} • {familyToSchedule.phone}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="schedule-date">Delivery Date</Label>
              <Input
                id="schedule-date"
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                min={getMinDate()}
                data-testid="schedule-date-input"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule-time">Delivery Time Slot</Label>
              <select
                id="schedule-time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="w-full h-11 px-3 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                data-testid="schedule-time-select"
              >
                <option value="">Select a time slot</option>
                {timeSlots.map(slot => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowScheduleModal(false); setFamilyToSchedule(null); }}
              className="rounded-full"
              data-testid="cancel-schedule-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleManualSchedule}
              disabled={scheduling || !scheduleDate || !scheduleTime}
              className="rounded-full"
              data-testid="confirm-schedule-button"
            >
              {scheduling ? 'Scheduling...' : 'Confirm & Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Delivered Modal */}
      <Dialog open={showDeliveredModal} onOpenChange={setShowDeliveredModal}>
        <DialogContent className="sm:max-w-md" data-testid="mark-delivered-dialog">
          <DialogHeader>
            <DialogTitle>Mark as Delivered</DialogTitle>
            <DialogDescription>
              Record that {familyToMarkDelivered?.parent_first_name} {familyToMarkDelivered?.parent_last_name} has received their bookshelf delivery.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {familyToMarkDelivered && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="font-medium text-green-900">{familyToMarkDelivered.parent_first_name} {familyToMarkDelivered.parent_last_name}</p>
                <p className="text-sm text-green-700">{familyToMarkDelivered.email}</p>
                <p className="text-sm text-green-700">{familyToMarkDelivered.address}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="delivered-date">Delivery Date</Label>
              <Input
                id="delivered-date"
                type="date"
                value={deliveredDate}
                onChange={(e) => setDeliveredDate(e.target.value)}
                data-testid="delivered-date-input"
                className="h-11"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowDeliveredModal(false); setFamilyToMarkDelivered(null); }}
              className="rounded-full"
              data-testid="cancel-delivered-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkDelivered}
              disabled={markingDelivered || !deliveredDate}
              className="rounded-full bg-green-600 hover:bg-green-700"
              data-testid="confirm-delivered-button"
            >
              {markingDelivered ? 'Saving...' : 'Confirm Delivered'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}